import { useState, useEffect } from 'react'
import { HardDrive, Database, Github, AlertTriangle, Cloud } from 'lucide-react'
import { useStorageStore } from '../store/useStorageStore'

function StorageStatusBar() {
  const { 
    kvPercentage, 
    d1Usage,
    isGithubLinked,
    githubRepo,
    githubQuota,
    fetchStatus: fetchStorageStatus,
    fetchGithubQuota
  } = useStorageStore()

  const [showD1Warning, setShowD1Warning] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    fetchStorageStatus()
    const interval = setInterval(fetchStorageStatus, 30000)
    return () => clearInterval(interval)
  }, [fetchStorageStatus])

  useEffect(() => {
    if (isGithubLinked) {
      fetchGithubQuota()
      const interval = setInterval(fetchGithubQuota, 60000)
      return () => clearInterval(interval)
    }
  }, [isGithubLinked, fetchGithubQuota])

  useEffect(() => {
    const d1Percentage = (d1Usage / (500 * 1024 * 1024)) * 100
    if (d1Percentage >= 90 && !dismissed) {
      setShowD1Warning(true)
    } else {
      setShowD1Warning(false)
    }
  }, [d1Usage, dismissed])

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 MB'
    const mb = bytes / (1024 * 1024)
    if (mb < 1024) return `${mb.toFixed(0)} MB`
    return `${(mb / 1024).toFixed(2)} GB`
  }

  const d1Percentage = (d1Usage / (500 * 1024 * 1024)) * 100

  const getD1Color = () => {
    if (d1Percentage >= 90) return 'bg-red-500'
    if (d1Percentage >= 75) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getD1TextColor = () => {
    if (d1Percentage >= 90) return 'text-red-400'
    if (d1Percentage >= 75) return 'text-yellow-400'
    return ''
  }

  const getQuotaColor = () => {
    if (githubQuota.remaining < 10) return 'text-red-400'
    if (githubQuota.remaining < 100) return 'text-yellow-400'
    return 'text-green-400'
  }

  return (
    <div className="h-statusbar border-t border-white/10 p-4 space-y-3">
      {/* D1 存储 */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <Database size={12} />
            <span>D1</span>
            {d1Percentage >= 90 && (
              <Cloud size={10} className="text-red-400" title="外链托管模式" />
            )}
          </div>
          <span className={getD1TextColor()}>
            {formatSize(d1Usage)}
            {d1Percentage >= 90 && ' (外链模式)'}
          </span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden relative">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${getD1Color()}`}
            style={{ width: `${Math.min(d1Percentage, 100)}%` }}
          />
          {d1Percentage >= 90 && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
          )}
        </div>
      </div>

      {/* D1 外链模式气泡提示 */}
      {showD1Warning && !dismissed && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-2 relative">
          <button
            onClick={() => {
              setDismissed(true)
              setShowD1Warning(false)
            }}
            className="absolute top-1 right-1 text-red-400 hover:text-red-300"
          >
            ×
          </button>
          <div className="flex items-start gap-2 pr-4">
            <AlertTriangle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-red-300">内容外链化模式已启用</p>
              <p className="text-xs text-red-400/80 mt-0.5">
                系统已进入"内容外链化"模式，长文本将自动托管至 GitHub 以节省数据库空间。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KV 存储 */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <HardDrive size={12} />
            <span>KV</span>
          </div>
          <span className={kvPercentage > 80 ? 'text-orange-400' : ''}>
            {kvPercentage.toFixed(1)}%
          </span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              kvPercentage > 80 
                ? 'bg-orange-500 animate-pulse-warning' 
                : kvPercentage > 60 
                  ? 'bg-yellow-500' 
                  : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(kvPercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* GitHub 状态 */}
      <div className="flex items-center gap-2 text-xs">
        <Github size={12} className={isGithubLinked ? 'text-green-400' : 'text-gray-500'} />
        <span className={isGithubLinked ? 'text-green-400' : 'text-gray-500'}>
          {isGithubLinked ? (githubRepo || '已连接') : '未绑定'}
        </span>
        {isGithubLinked && (
          <span className={getQuotaColor()}>
            Quota: {githubQuota.remaining}/{githubQuota.limit}
          </span>
        )}
      </div>

      {/* 悬停提示 */}
      {kvPercentage > 80 && !isGithubLinked && (
        <div className="text-xs text-orange-400 bg-orange-400/10 px-2 py-1 rounded">
          建议启用 GitHub 存储
        </div>
      )}
    </div>
  )
}

export default StorageStatusBar
